import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Smartphone, ArrowRight, ArrowLeft, Loader2, CheckCircle, BarChart3, TrendingUp } from 'lucide-react';
import { digitalLiteracyScanApi, DigitalLiteracyQuestion } from '../api/digitalLiteracyScan';

type ScanState = 'intro' | 'scan' | 'submitting' | 'results';

export const DigitalLiteracyScan: React.FC = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<ScanState>('intro');
  const [questions, setQuestions] = useState<DigitalLiteracyQuestion[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [attemptId, setAttemptId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  const currentQuestion = questions[currentQuestionIndex];
  const progress = questions.length > 0 ? ((currentQuestionIndex + 1) / questions.length) * 100 : 0;

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const data = await digitalLiteracyScanApi.getQuestions();
      setQuestions(data);
    } catch (err: any) {
      setError('Kon vragen niet laden. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const startScan = async () => {
    try {
      setLoading(true);
      
      // Generate a temporary user ID for anonymous users
      const tempUserId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const attempt = await digitalLiteracyScanApi.createAttempt(tempUserId);
      setAttemptId(attempt.id);
      setState('scan');
    } catch (err: any) {
      setError('Kon scan niet starten. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (rating: number) => {
    if (!currentQuestion) return;
    
    setAnswers(prev => ({
      ...prev,
      [currentQuestion.id]: rating
    }));
  };

  const nextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      submitScan();
    }
  };

  const previousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const submitScan = async () => {
    try {
      setState('submitting');
      setLoading(true);
      
      await digitalLiteracyScanApi.updateAttempt(attemptId, answers);
      setState('results');
    } catch (err: any) {
      setError('Kon scan niet opslaan. Probeer het later opnieuw.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getScoreLevel = (score: number) => {
    if (score >= 4.5) return { level: 'Expert', color: 'text-green-600', bgColor: 'bg-green-50' };
    if (score >= 3.5) return { level: 'Gevorderd', color: 'text-blue-600', bgColor: 'bg-blue-50' };
    if (score >= 2.5) return { level: 'Gemiddeld', color: 'text-yellow-600', bgColor: 'bg-yellow-50' };
    if (score >= 1.5) return { level: 'Basis', color: 'text-orange-600', bgColor: 'bg-orange-50' };
    return { level: 'Beginner', color: 'text-red-600', bgColor: 'bg-red-50' };
  };

  const getCategoryScore = (category: string) => {
    const categoryQuestions = questions.filter(q => q.category === category);
    const categoryAnswers = categoryQuestions
      .map(q => answers[q.id])
      .filter(answer => answer !== undefined);
    
    if (categoryAnswers.length === 0) return 0;
    return categoryAnswers.reduce((sum, answer) => sum + answer, 0) / categoryAnswers.length;
  };

  const getOverallScore = () => {
    const allAnswers = Object.values(answers);
    if (allAnswers.length === 0) return 0;
    return allAnswers.reduce((sum, answer) => sum + answer, 0) / allAnswers.length;
  };

  const categories = Array.from(new Set(questions.map(q => q.category)));

  if (loading && state === 'intro') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Vragen laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <p className="text-red-800 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
            >
              Opnieuw proberen
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Intro Screen
  if (state === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-blue-50">
        <div className="max-w-4xl mx-auto px-4 py-16">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Smartphone className="w-10 h-10 text-blue-600" />
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Digitale Geletterdheid Zelfscan
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Evalueer je digitale geletterdheid door onderstaande vragen te beantwoorden. 
                Geef voor elke vraag aan hoe goed de stelling jouw vaardigheden beschrijft op een schaal van 1 tot 5.
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                {error}
              </div>
            )}

            <div className="space-y-6 mb-8">
              {/* Score Legend */}
              <div className="bg-gray-50 rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="w-5 h-5 text-blue-600 mr-2" />
                  Betekenis scores
                </h2>
                <div className="grid grid-cols-5 gap-4">
                  {[
                    { score: 1, label: "Helemaal niet" },
                    { score: 2, label: "Weinig" },
                    { score: 3, label: "Gemiddeld" },
                    { score: 4, label: "Goed" },
                    { score: 5, label: "Uitstekend" }
                  ].map(({ score, label }) => (
                    <div key={score} className="text-center">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mx-auto mb-2">
                        {score}
                      </div>
                      <p className="text-xs text-gray-600 leading-tight">{label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 rounded-2xl p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Wat kun je verwachten?
                </h2>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>{questions.length} vragen</strong> verdeeld over {categories.length} digitale geletterdheid categorieën
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Categorieën:</strong> Basis Computer Vaardigheden, Internet & Netwerken, Digitale Communicatie, Informatie & Media, Privacy & Veiligheid, Productiviteit Tools
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>10-15 minuten</strong> om de scan te voltooien
                    </span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">
                      <strong>Persoonlijk profiel</strong> met scores per categorie en aanbevelingen
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={startScan}
                disabled={loading}
                className="flex-1 inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white rounded-2xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Laden...
                  </>
                ) : (
                  <>
                    Start Digitale Geletterdheid Scan
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </button>
              <button
                onClick={() => navigate('/')}
                className="px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-2xl font-semibold hover:border-blue-300 hover:bg-blue-50 transition-all duration-300"
              >
                Terug naar Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {state === 'scan' && currentQuestion && (
          <div>
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  Vraag {currentQuestionIndex + 1} van {questions.length}
                </span>
                <span className="text-sm font-medium text-gray-700">
                  {Math.round(progress)}% voltooid
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question Card */}
            <div className="bg-white rounded-3xl shadow-xl p-8 mb-6">
              <div className="mb-6">
                <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
                  {currentQuestion.category}
                </span>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {currentQuestion.statement}
                </h2>
                
                {currentQuestion.example && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    <p className="text-gray-700 leading-relaxed">
                      {currentQuestion.example}
                    </p>
                  </div>
                )}
              </div>

              {/* Rating Scale */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Hoe goed beschrijft deze stelling jouw vaardigheden?
                </h3>
                <div className="grid grid-cols-5 gap-3">
                  {[1, 2, 3, 4, 5].map((rating) => {
                    const isSelected = answers[currentQuestion.id] === rating;
                    
                    return (
                      <button
                        key={rating}
                        onClick={() => handleAnswer(rating)}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50 text-blue-900'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className="text-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-2 text-sm font-bold ${
                            isSelected
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {rating}
                          </div>
                          <div className="text-xs text-gray-600">
                            {rating === 1 && 'Helemaal niet'}
                            {rating === 2 && 'Weinig'}
                            {rating === 3 && 'Gemiddeld'}
                            {rating === 4 && 'Goed'}
                            {rating === 5 && 'Uitstekend'}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={previousQuestion}
                disabled={currentQuestionIndex === 0}
                className="inline-flex items-center px-6 py-3 bg-white text-gray-700 border border-gray-300 rounded-xl font-medium hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Vorige
              </button>

              {currentQuestionIndex === questions.length - 1 ? (
                <button
                  onClick={nextQuestion}
                  disabled={!answers[currentQuestion.id]}
                  className="inline-flex items-center px-8 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Bekijk Je Resultaten
                </button>
              ) : (
                <button
                  onClick={nextQuestion}
                  disabled={!answers[currentQuestion.id]}
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Volgende
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              )}
            </div>
          </div>
        )}

        {state === 'submitting' && (
          <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
            <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-xl text-gray-700 font-medium">Je profiel wordt berekend...</p>
          </div>
        )}

        {state === 'results' && (
          <div className="space-y-6">
            {/* Overall Score */}
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
              <div className="text-center mb-6">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">
                  Scan Voltooid!
                </h2>
                <p className="text-gray-600">
                  Hier zijn je resultaten voor digitale geletterdheid
                </p>
              </div>

              <div className="text-center">
                <div className="inline-flex items-center space-x-4 bg-gray-50 rounded-2xl p-6">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-gray-900">
                      {getOverallScore().toFixed(1)}
                    </div>
                    <div className="text-sm text-gray-600">Gemiddelde Score</div>
                  </div>
                  <div className="w-px h-16 bg-gray-300" />
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${getScoreLevel(getOverallScore()).color}`}>
                      {getScoreLevel(getOverallScore()).level}
                    </div>
                    <div className="text-sm text-gray-600">Niveau</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <BarChart3 className="w-5 h-5 mr-2" />
                Resultaten per Categorie
              </h3>
              
              <div className="grid gap-4">
                {categories.map((category) => {
                  const score = getCategoryScore(category);
                  const level = getScoreLevel(score);
                  
                  return (
                    <div key={category} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{category}</h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 max-w-xs">
                            <div 
                              className={`h-2 rounded-full transition-all duration-500 ${level.bgColor.replace('bg-', 'bg-').replace('-50', '-500')}`}
                              style={{ width: `${(score / 5) * 100}%` }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-600">
                            {score.toFixed(1)}
                          </span>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${level.bgColor} ${level.color}`}>
                        {level.level}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-3xl shadow-xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2" />
                Aanbevelingen
              </h3>
              
              <div className="space-y-4">
                {categories.map((category) => {
                  const score = getCategoryScore(category);
                  
                  if (score < 3) {
                    return (
                      <div key={category} className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                        <h4 className="font-medium text-orange-900 mb-2">
                          {category} - Verbetering Aanbevolen
                        </h4>
                        <p className="text-orange-800 text-sm">
                          Je score van {score.toFixed(1)} toont dat er ruimte is voor verbetering in deze categorie. 
                          Overweeg om training te volgen of meer te oefenen met de bijbehorende tools en vaardigheden.
                        </p>
                      </div>
                    );
                  } else if (score >= 4) {
                    return (
                      <div key={category} className="bg-green-50 border border-green-200 rounded-xl p-4">
                        <h4 className="font-medium text-green-900 mb-2">
                          {category} - Sterke Vaardigheden
                        </h4>
                        <p className="text-green-800 text-sm">
                          Uitstekend! Je score van {score.toFixed(1)} toont dat je sterke vaardigheden hebt in deze categorie. 
                          Overweeg om anderen te helpen of je kennis uit te breiden naar geavanceerde onderwerpen.
                        </p>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                Wat wil je nu doen?
              </h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/digital-assessment')}
                  className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Digitale Kennistoets
                </button>
                <button
                  onClick={() => navigate('/topics?theme=digital-literacy')}
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all shadow-lg hover:shadow-xl"
                >
                  Training Modules
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="inline-flex items-center justify-center px-6 py-3 bg-gray-600 text-white rounded-xl font-semibold hover:bg-gray-700 transition-all"
                >
                  Scan Opnieuw
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
